import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, Download, FileUp } from 'lucide-react';
import { StudentLayout } from '../../components/layout/StudentLayout';
import { studentExamApi } from '../../api/examApi';
import { checkSubmissionFile } from '../../lib/uploadLimits';
import UploadHint from '../../components/UploadHint';
import { toast } from 'sonner';
import { CorrectedPaperViewer } from '../../components/student/CorrectedPaperViewer';
import { parseApiError } from '../../lib/apiErrors';

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ExamResults() {
  const { attemptId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answerFile, setAnswerFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // useCallback so the effect below can declare it as a dependency honestly:
  // it only changes when attemptId does, so this still runs exactly once per
  // attempt, same as the previous [attemptId] dependency array.
  const loadResult = useCallback(async () => {
    try {
      const response = await studentExamApi.getAttemptDetail(attemptId);
      setResult(response.data);
    } catch (err) {
      toast.error('Failed to load result');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => {
    loadResult();
  }, [loadResult]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    const fileError = checkSubmissionFile(file);
    if (fileError) {
      toast.error(fileError);
      e.target.value = '';
      return;
    }
    setAnswerFile(file);
  };

  const handleUpload = async () => {
    if (!answerFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', answerFile);
      await studentExamApi.uploadAnswer(attemptId, formData);
      toast.success('Answer sheet uploaded successfully!');
      setAnswerFile(null);
      await loadResult();
    } catch (err) {
      toast.error(parseApiError(err, 'Upload failed').message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSubmission = async (submissionId, index) => {
    try {
      const res = await studentExamApi.downloadMySubmission(submissionId);
      downloadBlob(res.data, `submission_${index + 1}.pdf`);
    } catch {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <StudentLayout title="Exam Results">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-slate-200 dark:bg-slate-700 rounded-lg" />
          <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-lg" />
        </div>
      </StudentLayout>
    );
  }

  if (!result) {
    return (
      <StudentLayout title="Exam Results">
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-slate-400">Result not found</p>
          <Link to="/student" className="text-primary dark:text-accent-cool hover:underline mt-2 inline-block">
            Back to Dashboard
          </Link>
        </div>
      </StudentLayout>
    );
  }

  const percentage = result.total_score ? ((result.total_score / result.total_marks) * 100).toFixed(1) : null;
  const submissions = result.submissions || [];

  return (
    <StudentLayout title="Exam Results">
      <div className="animate-fade-in space-y-6 max-w-3xl">
        <Link
          to="/student"
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent-cool"
        >
          <ArrowLeft size={18} />
          Back to Dashboard
        </Link>

        {/* Result Card */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className={`p-6 ${
            result.status === 'evaluated' 
              ? 'bg-gradient-to-r from-primary to-primary-light' 
              : 'bg-gradient-to-r from-blue-500 to-blue-600'
          }`}>
            <div className="flex items-center justify-between text-white">
              <div>
                <h2 className="text-2xl font-bold">{result.exam_title}</h2>
                <p className="opacity-90">{result.exam_subject}{result.conduct_mode === 'offline' ? ' · Paper test' : ''}</p>
              </div>
              <div className="text-right">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  result.status === 'evaluated' 
                    ? 'bg-white/20' 
                    : 'bg-white/20'
                } capitalize`}>
                  {result.status.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {result.status === 'evaluated' ? (
              <>
                <div className={`grid grid-cols-1 gap-6 mb-6 ${result.conduct_mode === 'offline' ? '' : 'md:grid-cols-3'}`}>
                  {result.conduct_mode !== 'offline' && (<>
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">MCQ Score</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {result.mcq_score ?? '-'}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Subjective Score</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {result.subjective_score ?? '-'}
                    </p>
                  </div>
                  </>)}
                  <div className="text-center p-4 bg-primary/10 dark:bg-accent-cool/10 rounded-lg">
                    <p className="text-sm text-primary dark:text-accent-cool mb-1">Total Score</p>
                    <p className="text-3xl font-bold text-primary dark:text-accent-cool">
                      {result.total_score}/{result.total_marks}
                    </p>
                    {percentage && (
                      <p className="text-sm text-primary/70 dark:text-accent-cool/70 mt-1">
                        ({percentage}%)
                      </p>
                    )}
                  </div>
                </div>

                {result.feedback && (
                  <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10 p-4 mb-6">
                    <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
                      Faculty Feedback
                    </p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{result.feedback}</p>
                  </div>
                )}

                {result.corrected_paper_url && (
                  <div className="mb-6">
                    <h3 className="font-medium text-slate-900 dark:text-white mb-2">Corrected Paper</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">View only. This copy is watermarked with your name.</p>
                    <CorrectedPaperViewer attemptId={attemptId} className="max-h-[70vh]" />
                  </div>
                )}
              </>
            ) : (
              <div>
                <div className="text-center py-6">
                  <Clock className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    Pending Evaluation
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    Your exam has been submitted and is awaiting evaluation by the faculty.
                  </p>
                </div>

                {/* Answer sheet: the question paper stays locked after the timer, but
                    uploads stay open — show existing uploads and let the student add
                    more (up to 3) if evaluation hasn't started. */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                  <h3 className="font-medium text-slate-900 dark:text-white mb-3">Answer Sheet</h3>

                  {submissions.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {submissions.map((s, i) => (
                        <div
                          key={s.id}
                          className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm"
                        >
                          <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                            <CheckCircle size={14} className="text-emerald-500" />
                            Upload {i + 1}
                            {s.is_late && <span className="text-rose-500">(late)</span>}
                          </span>
                          <button
                            onClick={() => handleDownloadSubmission(s.id, i)}
                            className="inline-flex items-center gap-1.5 text-primary dark:text-accent-cool hover:underline"
                          >
                            <Download size={14} /> Download
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                      No answer sheet uploaded yet.
                    </p>
                  )}

                  {!result.submissions_open ? (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Submissions are currently closed by your faculty.
                    </p>
                  ) : submissions.length >= 3 ? (
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      Maximum of 3 uploads reached.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="late-answer-upload"
                      />
                      <label
                        htmlFor="late-answer-upload"
                        className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-primary dark:hover:border-accent-cool transition-colors text-sm"
                      >
                        <FileUp size={16} className="text-slate-400" />
                        <span className="text-slate-600 dark:text-slate-400">
                          {answerFile ? answerFile.name : <>Click to select a file (<UploadHint className="" />)</>}
                        </span>
                      </label>
                      <button
                        onClick={handleUpload}
                        disabled={!answerFile || uploading}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 text-sm"
                      >
                        {uploading ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Answer Summary */}
            {result.answers && result.answers.length > 0 && (
              <div>
                <h3 className="font-medium text-slate-900 dark:text-white mb-4">Your Answers</h3>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {result.answers.map((answer) => (
                    <div
                      key={answer.question_number}
                      className="flex flex-col items-center p-2 bg-slate-50 dark:bg-slate-900/50 rounded-lg"
                    >
                      <span className="text-xs text-slate-500 dark:text-slate-400">Q{answer.question_number}</span>
                      <span className="font-medium text-slate-900 dark:text-white">
                        {answer.selected_option || '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="text-slate-500 dark:text-slate-400">Started:</span>{' '}
              <span className="text-slate-900 dark:text-white">
                {result.start_time ? new Date(result.start_time).toLocaleString() : '-'}
              </span>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Submitted:</span>{' '}
              <span className="text-slate-900 dark:text-white">
                {result.end_time ? new Date(result.end_time).toLocaleString() : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
}
